import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { CreateStaffUserPayload, UpdateStaffUserPayload, StaffBulkImportResponse } from '../models/staff-users.models';

@Injectable({
  providedIn: 'root',
})
export class StaffUsersService {
  private baseAPI = environment.baseAPI;

  constructor(private http: HttpClient) { }

  getAllStaffUsers(
    organizationId: string,
    pageNumber: number = 1,
    pageSize: number = 20,
    search?: string,
    status?: string,
    employmentType?: string,
    departmentId?: string,
  ) {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    if (employmentType) params = params.set('employmentType', employmentType);
    if (departmentId) params = params.set('departmentId', departmentId);

    return this.http.get(
      `${this.baseAPI}/Staff/organisations/${organizationId}`,
      { params },
    );
  }

  getStaffSummary(fromDate?: string, toDate?: string) {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get(`${this.baseAPI}/Staff/summary`, { params });
  }

  getStaffUserById(userId: string) {
    return this.http.get(`${this.baseAPI}/IdentityManagement/users/${userId}`);
  }

  getStaffDetail(staffUserId: string) {
    return this.http.get(`${this.baseAPI}/Staff/${staffUserId}`);
  }

  getStaffBookingHistory(staffUserId: string, params: {
    month?: number;
    year?: number;
    status?: string;
    pageNumber?: number;
    pageSize?: number;
  }) {
    let httpParams = new HttpParams()
      .set('pageNumber', (params.pageNumber || 1).toString())
      .set('pageSize', (params.pageSize || 20).toString());

    if (params.month) httpParams = httpParams.set('month', params.month.toString());
    if (params.year) httpParams = httpParams.set('year', params.year.toString());
    if (params.status) httpParams = httpParams.set('status', params.status);

    return this.http.get(`${this.baseAPI}/Staff/${staffUserId}/bookings`, { params: httpParams });
  }

  exportStaffBookingHistory(staffUserId: string, params: {
    format: 'csv' | 'pdf';
    month?: number;
    year?: number;
    status?: string;
  }) {
    let httpParams = new HttpParams().set('format', params.format);
    if (params.month) httpParams = httpParams.set('month', params.month.toString());
    if (params.year) httpParams = httpParams.set('year', params.year.toString());
    if (params.status) httpParams = httpParams.set('status', params.status);

    return this.http.get(`${this.baseAPI}/Staff/${staffUserId}/bookings/export`, {
      params: httpParams,
      responseType: 'blob',
    });
  }

  createStaffUser(payload: CreateStaffUserPayload) {
    return this.http.post(`${this.baseAPI}/Staff`, payload);
  }

  updateStaffUser(payload: UpdateStaffUserPayload) {
    return this.http.put(`${this.baseAPI}/IdentityManagement/update-staff`, payload);
  }

  deactivateStaffUser(staffUserId: string, organisationId: string) {
    const params = new HttpParams()
      .set('organisationId', organisationId)
      .set('enable', 'false');
    return this.http.patch(`${this.baseAPI}/Staff/${staffUserId}/access`, {}, { params });
  }

  activateStaffUser(staffUserId: string, organisationId: string) {
    const params = new HttpParams()
      .set('organisationId', organisationId)
      .set('enable', 'true');
    return this.http.patch(`${this.baseAPI}/Staff/${staffUserId}/access`, {}, { params });
  }

  deleteStaffUser(staffUserId: string, organizationId: string) {
    const params = new HttpParams().set('organisationId', organizationId);

    return this.http.delete(
      `${this.baseAPI}/Staff/${staffUserId}`,
      { params }
    );
  }

  resendInvite(staffUserId: string) {
    return this.http.post(`${this.baseAPI}/Staff/${staffUserId}/resend-invitation`, {});
  }

  bulkImportStaffUsers(organisationId: string, importedByEmail: string, file: File) {
    const formData = new FormData();
    formData.append('OrganisationId', organisationId);
    formData.append('ImportedByEmail', importedByEmail);
    formData.append('File', file);

    return this.http.post<StaffBulkImportResponse>(`${this.baseAPI}/Staff/bulk-import`, formData);
  }

  getBulkImportTemplate(organisationId: string) {
    return this.http.get(`${this.baseAPI}/Staff/bulk-import/template`, {
      params: { organisationId },
      responseType: 'blob',
    });
  }
}
